import { DavetYenidenGonder } from "./DavetYenidenGonder";
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  kocsuzHesapSemasi,
  mentorumPaketSemasi,
  mentorumKoclugaGecisSemasi,
  mentorumErisimVerSemasi,
  mentorumErisimSonlandirSemasi,
  mentorumKonuSemasi,
  mentorumMedyaSemasi,
  mentorumKagitRizaSemasi,
  mentorumRizaBelgesiYanitiSemasi,
} from "@koclukpanel/contracts";
import { Alan, Buton, Kart, MetinAlani, Secim, Sekmeler } from "@koclukpanel/ui";
import { api, ApiHatasi, veliListesi } from "./api";
import { izinKapsami, izinSorgusu, type IzinHaritasi } from "./izin";
import { OgrenciSecici } from "./OgrenciSecici";
import { Hata403 } from "./SistemSayfalari";
import { useSudoKapisi } from "./SudoKapisi";

// F-MNT-13 — hassas yanıtlar ekrandan çıkınca önbellekte tutulmaz ve kendiliğinden yeniden okunmaz.
export const MENTORUM_OKUMA = {
  retry: false,
  gcTime: 0,
  staleTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

export function mentorumTarih(deger: string | null): string {
  return deger === null
    ? "—"
    : new Date(deger).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" });
}

export function MentorumHata({ hata }: { hata: unknown }) {
  if (!hata) return null;
  const iletiler: Record<string, string> = {
    MODUL_KAPALI: "Mentörüm kullanıma kapalı.",
    MENTORUM_ERISIM_YOK: "Öğrencinin Mentörüm erişim hakkı bulunmuyor.",
    MENTORUM_ONAYI_BEKLIYOR: "Öğrenci bilgilendirme onayı bekleniyor.",
    VELI_ONAYI_BEKLIYOR: "Veli onayı bekleniyor.",
    BULUNAMADI: "Kayıt bulunamadı veya erişiminiz yok.",
  };
  return (
    <p role="alert" className="text-hata-700 text-sm break-words">
      {hata instanceof ApiHatasi
        ? (iletiler[hata.kod] ?? hata.message)
        : "İşlem tamamlanamadı. Lütfen yeniden deneyin."}
    </p>
  );
}

export function MentorumOgrenciSec({
  izinler,
  onSec,
}: {
  izinler: IzinHaritasi;
  onSec: (kimlik: string) => void;
}) {
  const [kimlik, setKimlik] = useState("");
  if (izinSorgusu(izinler)("kisi.ogrenci.goruntule")) {
    return (
      <OgrenciSecici
        baslik="Öğrenci seçin"
        aciklama="Mentörüm işlemleri için öğrenciyi seçin."
        onSec={(id) => onSec(id)}
      />
    );
  }
  return (
    <form
      className="flex min-w-0 flex-col gap-3"
      onSubmit={(olay) => {
        olay.preventDefault();
        if (kimlik.trim()) onSec(kimlik.trim());
      }}
    >
      <Alan
        etiket="Öğrenci kimliği"
        value={kimlik}
        onChange={(olay) => setKimlik(olay.target.value)}
        zorunlu
      />
      <Buton type="submit" disabled={!kimlik.trim()}>
        Öğrenciyi aç
      </Buton>
    </form>
  );
}

type AlanTanimi = {
  ad: string;
  etiket: string;
  tur?: string;
  secenekler?: readonly (readonly [string, string])[];
  istegeBagli?: boolean;
};

// F-MNT-03/06 — istemci yalnız sözleşme biçimini doğrular; süre, kapsam ve içerik sınırlarını sunucu belirler.
export function MentorumForm({
  baslik,
  alanlar,
  gonder,
  dugme = "Kaydet",
  tamamlandi,
  ilkDegerler = {},
  onayMetni,
}: {
  baslik: string;
  alanlar: readonly AlanTanimi[];
  gonder: (degerler: Record<string, string>, dosya: File | undefined) => Promise<unknown>;
  dugme?: string;
  tamamlandi?: () => void;
  ilkDegerler?: Record<string, string>;
  onayMetni?: string;
}) {
  const [degerler, setDegerler] = useState<Record<string, string>>(ilkDegerler);
  const [dosya, setDosya] = useState<File>();
  const [surum, setSurum] = useState(0);
  const [basarili, setBasarili] = useState(false);
  const [onayGirdisi, setOnayGirdisi] = useState<{
    degerler: Record<string, string>;
    dosya: File | undefined;
  } | null>(null);
  const { hataYakala, kapi } = useSudoKapisi();
  const islem = useMutation({
    gcTime: 0,
    mutationFn: (girdi: { degerler: Record<string, string>; dosya: File | undefined }) =>
      gonder(girdi.degerler, girdi.dosya),
    onError: (hata, girdi) => hataYakala(() => islem.mutate(girdi))(hata),
    onSuccess: () => {
      setBasarili(true);
      setDegerler({});
      setDosya(undefined);
      setSurum((eski) => eski + 1);
      islem.reset();
      tamamlandi?.();
    },
  });
  return (
    <Kart baslik={baslik}>
      <div className="min-w-0">
        {kapi}
        <form
          key={surum}
          className="flex min-w-0 flex-col gap-3"
          onSubmit={(olay) => {
            olay.preventDefault();
            setBasarili(false);
            const girdi = { degerler: { ...degerler }, dosya };
            if (onayMetni) setOnayGirdisi(girdi);
            else islem.mutate(girdi);
          }}
        >
          {alanlar.map((alan) => {
            const ortak = {
              etiket: alan.etiket,
              value: degerler[alan.ad] ?? "",
              zorunlu: !alan.istegeBagli,
              onChange: (olay: { target: { value: string } }) => {
                setBasarili(false);
                setDegerler({ ...degerler, [alan.ad]: olay.target.value });
              },
            };
            if (alan.tur === "file")
              return (
                <Alan
                  key={alan.ad}
                  etiket={alan.etiket}
                  type="file"
                  onChange={(olay) => setDosya(olay.target.files?.[0])}
                  zorunlu={!alan.istegeBagli}
                />
              );
            if (alan.secenekler)
              return (
                <Secim key={alan.ad} {...ortak}>
                  <option value="">Seçin</option>
                  {alan.secenekler.map(([deger, etiket]) => (
                    <option key={deger} value={deger}>
                      {etiket}
                    </option>
                  ))}
                </Secim>
              );
            if (alan.tur === "textarea") return <MetinAlani key={alan.ad} {...ortak} />;
            return <Alan key={alan.ad} {...ortak} type={alan.tur ?? "text"} />;
          })}
          <MentorumHata hata={islem.error} />
          {basarili && <p role="status">İşlem tamamlandı.</p>}
          <Buton type="submit" disabled={islem.isPending || !!onayGirdisi}>
            {islem.isPending ? "Kaydediliyor…" : dugme}
          </Buton>
        </form>
        {onayGirdisi && (
          <div
            role="alertdialog"
            aria-label="Koçluğa geçiş onayı"
            tabIndex={-1}
            ref={(eleman) => eleman?.focus()}
            onKeyDown={(olay) => {
              if (olay.key === "Escape") setOnayGirdisi(null);
            }}
            className="border-uyari-200 bg-uyari-50 mt-3 flex flex-col gap-3 rounded-lg border p-3"
          >
            <p>{onayMetni}</p>
            <div className="flex flex-wrap gap-2">
              <Buton
                className="min-h-11"
                onClick={() => {
                  islem.mutate(onayGirdisi);
                  setOnayGirdisi(null);
                }}
              >
                Evet, koçluğa geçir
              </Buton>
              <Buton className="min-h-11" gorunum="ikincil" onClick={() => setOnayGirdisi(null)}>
                Vazgeç
              </Buton>
            </div>
          </div>
        )}
      </div>
    </Kart>
  );
}

function dogrula<T>(
  sema: {
    safeParse: (
      girdi: unknown,
    ) =>
      | { success: true; data: T }
      | { success: false; error: { issues: readonly { code?: string; message: string }[] } };
  },
  deger: unknown,
): T {
  const sonuc = sema.safeParse(deger);
  if (!sonuc.success)
    throw new ApiHatasi(
      400,
      "DOGRULAMA",
      ["invalid_type", "invalid_enum_value"].includes(sonuc.error.issues[0]?.code ?? "")
        ? "Zorunlu alanları ve seçimleri kontrol edin."
        : (sonuc.error.issues[0]?.message ?? "Bilgileri kontrol edin."),
    );
  return sonuc.data;
}

function govdeGonder(yol: string, govde: unknown, yontem: "POST" | "PUT" = "POST") {
  return api(yol, { yontem, govde });
}

interface Paket {
  id: string;
  name: string;
  durationDays: number | null;
}
interface Konu {
  id: string;
  title: string;
  description: string | null;
  authorName: string;
  authorTitle: string;
  sortOrder: number;
  publishedAt: string | null;
  media: { id: string; title: string; kind: "VIDEO" | "AUDIO" }[];
}
const SINIFLAR = [
  ["G5", "5. sınıf"],
  ["G6", "6. sınıf"],
  ["G7", "7. sınıf"],
  ["G8", "8. sınıf"],
  ["G9", "9. sınıf"],
  ["G10", "10. sınıf"],
  ["G11", "11. sınıf"],
  ["G12", "12. sınıf"],
  ["GRADUATE", "Mezun"],
  ["OTHER", "Diğer"],
] as const;

export function MentorumYonetim({ izinler }: { izinler: IzinHaritasi }) {
  const izinVar = izinSorgusu(izinler);
  const bolumler = [
    ...(izinVar("mentorum.erisim.goruntule")
      ? [{ deger: "durum", etiket: "Paket ve erişim durumu" }]
      : []),
    ...(izinVar("mentorum.erisim.yonet")
      ? [{ deger: "paket", etiket: "Paket ve hesap yönetimi" }]
      : []),
    ...(izinVar("mentorum.icerik.yonet") ? [{ deger: "icerik", etiket: "İçerik yönetimi" }] : []),
    ...(izinVar("kvkk.riza.kagit_kaydet") ? [{ deger: "onay", etiket: "Kâğıt veli onayı" }] : []),
  ];
  const [secili, setSecili] = useState("");
  const etkin = bolumler.some((bolum) => bolum.deger === secili) ? secili : bolumler[0]?.deger;
  if (!etkin) return <Hata403 onBugun={() => window.location.assign("/")} />;
  return (
    <div className="flex min-w-0 flex-col gap-4 break-words">
      <h2 className="text-notr-900 text-xl font-semibold">Mentörüm yönetimi</h2>
      {bolumler.length > 1 && (
        <Sekmeler
          etiket="Mentörüm yönetim bölümleri"
          sekmeler={bolumler}
          secili={etkin}
          onSec={setSecili}
        />
      )}
      {etkin === "durum" && <ErisimDurumu izinler={izinler} />}
      {etkin === "paket" && <PaketHesapYonetimi izinler={izinler} />}
      {etkin === "icerik" && <IcerikYonetimi />}
      {etkin === "onay" && <KagitOnay izinler={izinler} />}
    </div>
  );
}

function ErisimDurumu({ izinler }: { izinler: IzinHaritasi }) {
  const [ogrenciId, setOgrenciId] = useState("");
  const durum = useQuery({
    queryKey: ["mentorum", "erisim", ogrenciId],
    queryFn: ({ signal }) =>
      api<{ var: boolean; baslangic: string | null; bitis: string | null }>(
        `/mentorum/erisim/${encodeURIComponent(ogrenciId)}`,
        { signal },
      ),
    enabled: !!ogrenciId,
    ...MENTORUM_OKUMA,
  });
  if (!ogrenciId) return <MentorumOgrenciSec izinler={izinler} onSec={setOgrenciId} />;
  return (
    <Kart baslik="Paket ve erişim durumu">
      <Buton gorunum="ikincil" onClick={() => setOgrenciId("")}>
        Öğrenci değiştir
      </Buton>
      {durum.isPending ? (
        <p role="status">Durum yükleniyor…</p>
      ) : durum.isError ? (
        <MentorumHata hata={durum.error} />
      ) : (
        <dl className="mt-3 grid min-w-0 gap-2">
          <dt>Erişim</dt>
          <dd>{durum.data.var ? "Açık" : "Kapalı"}</dd>
          <dt>Başlangıç</dt>
          <dd>{mentorumTarih(durum.data.baslangic)}</dd>
          <dt>Bitiş</dt>
          <dd>{mentorumTarih(durum.data.bitis)}</dd>
        </dl>
      )}
      {izinKapsami(izinler, "mentorum.erisim.yonet") === "ALL" &&
        izinSorgusu(izinler)("sistem.rol.ata") &&
        izinKapsami(izinler, "kisi.ogrenci.duzenle") === "ALL" &&
        izinKapsami(izinler, "finans.tahsilat.isle") === "ALL" && (
          <KoclugaGecis key={ogrenciId} ogrenciId={ogrenciId} />
        )}
    </Kart>
  );
}

function KoclugaGecis({ ogrenciId }: { ogrenciId: string }) {
  const [gecisTamamlandi, setGecisTamamlandi] = useState(false);
  const istemci = useQueryClient();
  const durum = useQuery({
    queryKey: ["mentorum", "kocluga-gecis", ogrenciId],
    queryFn: ({ signal }) =>
      api<{ uygun: boolean }>(`/mentorum/kocluga-gecis/${encodeURIComponent(ogrenciId)}`, {
        signal,
      }),
    ...MENTORUM_OKUMA,
  });
  const koclar = useQuery({
    queryKey: ["mentorum", "gecis-koclari", ogrenciId],
    queryFn: ({ signal }) =>
      api<{ coachId: string; ad: string }[]>(
        `/ogrenciler/${encodeURIComponent(ogrenciId)}/atanabilir-uzmanlar`,
        { signal },
      ),
    enabled: durum.data?.uygun === true,
    ...MENTORUM_OKUMA,
  });
  const paketler = useQuery({
    queryKey: ["mentorum", "gecis-paketleri", ogrenciId],
    queryFn: ({ signal }) =>
      api<{ id: string; name: string; isActive: boolean }[]>("/finans/paketler", { signal }),
    enabled: durum.data?.uygun === true,
    ...MENTORUM_OKUMA,
  });
  if (durum.isError) return <MentorumHata hata={durum.error} />;
  if (!durum.data?.uygun)
    return gecisTamamlandi ? <p role="status">Öğrenci koçluğa geçirildi.</p> : null;
  if (koclar.isError || paketler.isError)
    return <MentorumHata hata={koclar.error ?? paketler.error} />;
  if (koclar.isPending || paketler.isPending) return <p>Geçiş seçenekleri yükleniyor…</p>;
  return (
    <MentorumForm
      baslik="Koçluğa geçiş"
      dugme="Koçluğa geçir"
      onayMetni="Öğrenciye koç ve abonelik atanacak. Koçsuz dönemin Mentörüm kayıtları koça kapalı kalacak. Geçişi onaylıyor musunuz?"
      alanlar={[
        {
          ad: "coachId",
          etiket: "Atanacak koç",
          secenekler: koclar.data.map((koc) => [koc.coachId, koc.ad]),
        },
        {
          ad: "paketId",
          etiket: "Koçluk paketi",
          secenekler: paketler.data
            .filter((paket) => paket.isActive)
            .map((paket) => [paket.id, paket.name]),
        },
        { ad: "baslangic", etiket: "Abonelik başlangıcı", tur: "date" },
        { ad: "bitis", etiket: "Abonelik bitişi", tur: "date" },
        { ad: "indirim", etiket: "İndirim (TL)", istegeBagli: true },
        { ad: "taksitSayisi", etiket: "Taksit sayısı", tur: "number" },
        { ad: "ilkVade", etiket: "İlk vade", tur: "date" },
      ]}
      gonder={(deger) => {
        const { coachId, ...abonelik } = deger;
        const govde = {
          coachId,
          abonelik: {
            ...abonelik,
            indirim: abonelik.indirim || null,
            taksitSayisi: Number(abonelik.taksitSayisi),
          },
        };
        dogrula(mentorumKoclugaGecisSemasi, govde);
        return govdeGonder(`/mentorum/kocluga-gecis/${encodeURIComponent(ogrenciId)}`, govde);
      }}
      tamamlandi={() => {
        setGecisTamamlandi(true);
        void istemci.invalidateQueries({ queryKey: ["mentorum"] });
      }}
    />
  );
}

function PaketHesapYonetimi({ izinler }: { izinler: IzinHaritasi }) {
  const ogrenciListeleyebilir = izinSorgusu(izinler)("kisi.ogrenci.goruntule");
  const [seciliOgrenciId, setSeciliOgrenciId] = useState("");
  const istemci = useQueryClient();
  const [erisimId, setErisimId] = useState("");
  const [yeniHesap, setYeniHesap] = useState<{
    id: string;
    studentNo: string;
    davetGonderildi: boolean;
  } | null>(null);
  const paketler = useQuery({
    queryKey: ["mentorum", "paket"],
    queryFn: ({ signal }) => api<Paket[]>("/mentorum/paket", { signal }),
    ...MENTORUM_OKUMA,
  });
  const paketSecenekleri = (paketler.data ?? []).map((paket) => [paket.id, paket.name] as const);
  const tazele = () => void istemci.invalidateQueries({ queryKey: ["mentorum", "paket"] });
  return (
    <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
      <Kart baslik="Tanımlı paketler">
        {paketler.isPending ? (
          <p>Yükleniyor…</p>
        ) : paketler.isError ? (
          <MentorumHata hata={paketler.error} />
        ) : paketler.data.length ? (
          <ul className="space-y-2">
            {paketler.data.map((paket) => (
              <li key={paket.id}>
                {paket.name} ·{" "}
                {paket.durationDays === null
                  ? "Koçluk üyeliği süresince"
                  : `${paket.durationDays} gün`}
              </li>
            ))}
          </ul>
        ) : (
          <p>Henüz paket tanımlanmadı.</p>
        )}
      </Kart>
      <MentorumForm
        baslik="Paket tanımla"
        alanlar={[
          { ad: "name", etiket: "Paket adı" },
          { ad: "description", etiket: "Paket açıklaması", tur: "textarea", istegeBagli: true },
          { ad: "durationDays", etiket: "Paket süresi (gün)", tur: "number", istegeBagli: true },
        ]}
        gonder={(deger) =>
          govdeGonder(
            "/mentorum/paket",
            dogrula(mentorumPaketSemasi, {
              name: deger.name,
              description: deger.description,
              durationDays: deger.durationDays ? Number(deger.durationDays) : null,
            }),
          )
        }
        tamamlandi={tazele}
      />
      <MentorumForm
        baslik="Koçsuz hesap aç"
        dugme="Hesap aç ve davet gönder"
        alanlar={[
          { ad: "firstName", etiket: "Ad" },
          { ad: "lastName", etiket: "Soyad" },
          { ad: "email", etiket: "E-posta", tur: "email" },
          { ad: "phone", etiket: "Telefon", tur: "tel", istegeBagli: true },
          { ad: "birthDate", etiket: "Doğum tarihi", tur: "date" },
          { ad: "gradeLevel", etiket: "Sınıf", secenekler: SINIFLAR },
          { ad: "packageId", etiket: "Paket", secenekler: paketSecenekleri, istegeBagli: true },
        ]}
        gonder={async (deger) => {
          const sonuc = await api<{ id: string; studentNo: string; davetGonderildi: boolean }>(
            "/mentorum/kocsuz-hesap",
            {
              yontem: "POST",
              govde: dogrula(kocsuzHesapSemasi, {
                ...deger,
                packageId: deger.packageId || null,
                phone: deger.phone || null,
              }),
            },
          );
          setYeniHesap(sonuc);
        }}
      />
      {yeniHesap && (
        <Kart baslik="Hesap açıldı">
          {yeniHesap.davetGonderildi ? (
            <p>Davet gönderildi.</p>
          ) : (
            <p role="alert" className="text-uyari-800 text-sm">
              Davet postası gönderilemedi — yeniden gönderin
            </p>
          )}
          {!yeniHesap.davetGonderildi && (
            <DavetYenidenGonder
              hedef={{ ogrenciId: yeniHesap.id }}
              izin="mentorum.erisim.yonet"
              tumKayitlarGerekli
              onGonderildi={() => setYeniHesap({ ...yeniHesap, davetGonderildi: true })}
            />
          )}
          <p>Öğrenci numarası: {yeniHesap.studentNo}</p>
          <p className="break-all">Öğrenci kimliği: {yeniHesap.id}</p>
        </Kart>
      )}
      <Kart baslik="Erişim ver">
        {ogrenciListeleyebilir && !seciliOgrenciId ? (
          <MentorumOgrenciSec izinler={izinler} onSec={setSeciliOgrenciId} />
        ) : (
          <>
            {ogrenciListeleyebilir && (
              <Buton gorunum="ikincil" onClick={() => setSeciliOgrenciId("")}>
                Öğrenci değiştir
              </Buton>
            )}
            <MentorumForm
              baslik="Öğrenciye erişim tanımla"
              key={seciliOgrenciId}
              alanlar={[
                ...(!ogrenciListeleyebilir ? [{ ad: "studentId", etiket: "Öğrenci kimliği" }] : []),
                {
                  ad: "packageId",
                  etiket: "Erişim paketi",
                  secenekler: paketSecenekleri,
                  istegeBagli: true,
                },
                {
                  ad: "kind",
                  etiket: "Erişim türü",
                  secenekler: [
                    ["COACHING", "Koçluk"],
                    ["STANDALONE", "Koçsuz"],
                  ],
                },
              ]}
              gonder={async (deger) => {
                const sonuc = await api<{ id: string }>("/mentorum/erisim", {
                  yontem: "POST",
                  govde: dogrula(mentorumErisimVerSemasi, {
                    ...deger,
                    studentId: seciliOgrenciId || deger.studentId,
                    packageId: deger.packageId || null,
                  }),
                });
                setErisimId(sonuc.id);
                return undefined;
              }}
            />
          </>
        )}
      </Kart>
      <Kart baslik="Erişim işlemleri">
        <Alan
          etiket="Erişim kimliği"
          value={erisimId}
          onChange={(olay) => setErisimId(olay.target.value)}
        />
        {erisimId.trim() && (
          <div className="mt-3 flex flex-col gap-4">
            <MentorumForm
              baslik="Koçsuz erişimi uzat"
              dugme="Bir ay uzat"
              alanlar={[]}
              gonder={() =>
                govdeGonder(`/mentorum/erisim/${encodeURIComponent(erisimId.trim())}/uzat`, {})
              }
            />
            <MentorumForm
              baslik="Erişimi sonlandır"
              dugme="Erişimi sonlandır"
              alanlar={[{ ad: "reason", etiket: "Sonlandırma gerekçesi", tur: "textarea" }]}
              gonder={(deger) =>
                govdeGonder(
                  `/mentorum/erisim/${encodeURIComponent(erisimId.trim())}/sonlandir`,
                  dogrula(mentorumErisimSonlandirSemasi, deger),
                )
              }
            />
          </div>
        )}
      </Kart>
    </div>
  );
}

function IcerikYonetimi() {
  const istemci = useQueryClient();
  const konular = useQuery({
    queryKey: ["mentorum", "icerik"],
    queryFn: ({ signal }) => api<Konu[]>("/mentorum/icerik/yonetim", { signal }),
    ...MENTORUM_OKUMA,
  });
  const [duzenlenen, setDuzenlenen] = useState<Konu | null>(null);
  const tazele = () => void istemci.invalidateQueries({ queryKey: ["mentorum", "icerik"] });
  const konuAlanlari: AlanTanimi[] = [
    { ad: "title", etiket: "Konu başlığı" },
    { ad: "description", etiket: "Konu açıklaması", tur: "textarea", istegeBagli: true },
    { ad: "authorName", etiket: "Hazırlayanın adı" },
    { ad: "authorTitle", etiket: "Hazırlayanın unvanı" },
    { ad: "sortOrder", etiket: "Konu sırası", tur: "number", istegeBagli: true },
  ];
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <MentorumForm
        baslik="Konu ekle"
        alanlar={konuAlanlari}
        gonder={(deger) =>
          govdeGonder(
            "/mentorum/icerik/konular",
            dogrula(mentorumKonuSemasi, { ...deger, sortOrder: Number(deger.sortOrder || 0) }),
          )
        }
        tamamlandi={tazele}
      />
      {konular.isPending ? (
        <p>İçerikler yükleniyor…</p>
      ) : konular.isError ? (
        <MentorumHata hata={konular.error} />
      ) : konular.data.length === 0 ? (
        <p>Henüz konu eklenmedi.</p>
      ) : (
        konular.data.map((konu) => (
          <Kart key={konu.id} baslik={konu.title}>
            <div className="flex min-w-0 flex-col gap-3">
              <p>
                {konu.authorName} · {konu.authorTitle}
              </p>
              <p>{konu.publishedAt ? "Yayında" : "Yayında değil"}</p>
              <p>{konu.description}</p>
              <Buton gorunum="ikincil" onClick={() => setDuzenlenen(konu)}>
                Konuyu düzenle
              </Buton>
              {duzenlenen?.id === konu.id && (
                <MentorumForm
                  ilkDegerler={{
                    title: konu.title,
                    description: konu.description ?? "",
                    authorName: konu.authorName,
                    authorTitle: konu.authorTitle,
                    sortOrder: String(konu.sortOrder),
                  }}
                  baslik="Konu bilgilerini değiştir"
                  alanlar={konuAlanlari}
                  gonder={(deger) =>
                    govdeGonder(
                      `/mentorum/icerik/${encodeURIComponent(konu.id)}`,
                      dogrula(mentorumKonuSemasi, {
                        ...deger,
                        sortOrder: Number(deger.sortOrder || 0),
                      }),
                      "PUT",
                    )
                  }
                  tamamlandi={() => {
                    setDuzenlenen(null);
                    tazele();
                  }}
                />
              )}
              <MentorumForm
                baslik="Yayın durumu"
                dugme={konu.publishedAt ? "Yayından kaldır" : "Yayınla"}
                alanlar={[]}
                gonder={() =>
                  govdeGonder(
                    `/mentorum/icerik/${encodeURIComponent(konu.id)}/${konu.publishedAt ? "yayindan-kaldir" : "yayinla"}`,
                    {},
                  )
                }
                tamamlandi={tazele}
              />
              <MentorumForm
                baslik="Medya yükle"
                dugme="Dosyayı yükle"
                alanlar={[
                  { ad: "title", etiket: "Medya başlığı" },
                  {
                    ad: "kind",
                    etiket: "Medya türü",
                    secenekler: [
                      ["VIDEO", "Video"],
                      ["AUDIO", "Ses"],
                    ],
                  },
                  {
                    ad: "durationSeconds",
                    etiket: "Süre (saniye)",
                    tur: "number",
                    istegeBagli: true,
                  },
                  { ad: "sortOrder", etiket: "Medya sırası", tur: "number", istegeBagli: true },
                  { ad: "dosya", etiket: "Video veya ses dosyası", tur: "file" },
                ]}
                gonder={async (deger, dosya) => {
                  if (!dosya)
                    throw new ApiHatasi(400, "DOSYA_YOK", "Video veya ses dosyası seçin.");
                  const veri = dogrula(mentorumMedyaSemasi, {
                    ...deger,
                    topicId: konu.id,
                    durationSeconds: deger.durationSeconds ? Number(deger.durationSeconds) : null,
                    sortOrder: Number(deger.sortOrder || 0),
                  });
                  const govde = new FormData();
                  govde.append("dosya", dosya);
                  govde.append("title", veri.title);
                  govde.append("kind", veri.kind);
                  govde.append("sortOrder", String(veri.sortOrder));
                  if (veri.durationSeconds)
                    govde.append("durationSeconds", String(veri.durationSeconds));
                  await govdeGonder(`/mentorum/icerik/${encodeURIComponent(konu.id)}/medya`, govde);
                }}
                tamamlandi={tazele}
              />
              {konu.media.map((medya) => (
                <MentorumForm
                  key={medya.id}
                  baslik={`${medya.kind === "VIDEO" ? "Video" : "Ses"}: ${medya.title}`}
                  dugme="Medyayı arşivle"
                  alanlar={[]}
                  gonder={() =>
                    govdeGonder(
                      `/mentorum/icerik/${encodeURIComponent(konu.id)}/medya/${encodeURIComponent(medya.id)}/arsivle`,
                      {},
                    )
                  }
                  tamamlandi={tazele}
                />
              ))}
              <MentorumForm
                baslik="Konu arşivi"
                dugme="Konuyu arşivle"
                alanlar={[]}
                gonder={() =>
                  govdeGonder(`/mentorum/icerik/${encodeURIComponent(konu.id)}/arsivle`, {})
                }
                tamamlandi={tazele}
              />
            </div>
          </Kart>
        ))
      )}
    </div>
  );
}

function KagitOnay({ izinler }: { izinler: IzinHaritasi }) {
  const [ogrenciId, setOgrenciId] = useState("");
  const belgeListeleyebilir = izinSorgusu(izinler)("kvkk.metin.yonet");
  const veliListeleyebilir = izinSorgusu(izinler)("kisi.veli.goruntule");
  const veliler = useQuery({
    queryKey: ["mentorum", "veliler", ogrenciId],
    queryFn: () => veliListesi(ogrenciId),
    enabled: !!ogrenciId && veliListeleyebilir,
    ...MENTORUM_OKUMA,
  });
  // F-MNT-05 — rıza durum ucu öğrenci/veliye aittir; personel yalnız yetkiliyse belge yönetim listesini okur.
  const liste = useQuery({
    queryKey: ["mentorum", "riza-belgeleri"],
    queryFn: async ({ signal }) =>
      mentorumRizaBelgesiYanitiSemasi
        .array()
        .parse(await api("/mentorum/riza-belgeleri", { signal })),
    enabled: !!ogrenciId && belgeListeleyebilir,
    ...MENTORUM_OKUMA,
  });
  const { sorguKapisi } = useSudoKapisi();
  if (!ogrenciId) return <MentorumOgrenciSec izinler={izinler} onSec={setOgrenciId} />;
  const belgeler = (liste.data ?? []).filter((belge) => belge.kind === "PARENT_CONSENT");
  let icerik: ReactNode;
  if (belgeListeleyebilir && liste.isPending) icerik = <p>Onay belgeleri yükleniyor…</p>;
  else if (veliListeleyebilir && veliler.isPending) icerik = <p>Veliler yükleniyor…</p>;
  else if (veliListeleyebilir && veliler.isError) icerik = <MentorumHata hata={veliler.error} />;
  else if (belgeListeleyebilir && liste.isError)
    icerik = sorguKapisi(liste.error, () => void liste.refetch()) ?? (
      <MentorumHata hata={liste.error} />
    );
  else
    icerik = (
      <MentorumForm
        key={ogrenciId}
        baslik="Kâğıt veli onayını kaydet"
        alanlar={[
          {
            ad: "documentId",
            etiket: belgeListeleyebilir ? "Veli rıza belgesi" : "Veli rıza belgesi kimliği",
            ...(belgeListeleyebilir
              ? {
                  secenekler: belgeler.map(
                    (belge) => [belge.id, `${belge.title} · ${belge.version}`] as const,
                  ),
                }
              : {}),
          },
          {
            ad: "veliUserId",
            etiket: veliListeleyebilir ? "Yasal veli" : "Yasal veli kullanıcı kimliği",
            ...(veliListeleyebilir
              ? {
                  secenekler: (veliler.data ?? [])
                    .filter((veli) => veli.isLegalGuardian)
                    .map(
                      (veli) => [veli.parentUserId, `${veli.firstName} ${veli.lastName}`] as const,
                    ),
                }
              : {}),
          },
          { ad: "reason", etiket: "Kâğıt onay gerekçesi", tur: "textarea" },
          { ad: "dosya", etiket: "Onay taraması (isteğe bağlı)", tur: "file", istegeBagli: true },
        ]}
        gonder={async (deger, dosya) => {
          const veri = dogrula(mentorumKagitRizaSemasi, deger);
          const govde = new FormData();
          Object.entries(veri).forEach(([ad, deger]) => govde.append(ad, deger));
          if (dosya) govde.append("dosya", dosya);
          await govdeGonder(`/mentorum/riza/${encodeURIComponent(ogrenciId)}/kagit`, govde);
        }}
      />
    );
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Buton gorunum="ikincil" onClick={() => setOgrenciId("")}>
        Öğrenci değiştir
      </Buton>
      {icerik}
    </div>
  );
}
